/* eslint-disable id-length */
/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable func-names */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// import { ImageFile } from '../models/image-file.interface';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../../environments/environment';
import {
  ResponseUploadMultipleFiles,
  ResponseGetImageInfo,
  GetDownloadFileRequest,
  GetDownloadFilesRequest,
  GetDownloadLinkRequest,
  GenerateImagesRequest,
} from './http.inerface';
import { DetectionOptions } from '../../object-detection/components/options/options.interface';

/**
 * Service for splitting images into multiple segments.

 */
@Injectable({
  providedIn: 'root',
})
export class ObjectDetectionService {
  private BASE_URL: string = environment.baseUrl;
  outputFormat: any = null;

  /**
   * Holds the unique identifier for the image upload.
   */
  idUpload = '';

  /**
   * Constructs the ImageService.
   * @param {HttpClient} http - Angular's HTTP client for making API calls.
   */
  constructor(private http: HttpClient) {}

  /**
   * Generates a UUID.
   * The generated UUID is in the pattern 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.
   * @returns {string} The generated UUID.
   * @example
   * const newUUID = this.imageService.generateUUID();
   */
  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;

        return v.toString(16);
      }
    );
  }

  /**
   * Uploads multiple image files.
   *
   * @param {File[]} images - The list of image files to upload.
   * @returns {Observable<ResponseUploadMultipleFiles[]>} An Observable that resolves to an array of upload responses.
   */
  uploadFiles(images: File[]): Observable<ResponseUploadMultipleFiles[]> {
    const formData: FormData = new FormData();
    if (!this.idUpload) this.idUpload = this.generateUUID();

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      formData.append('UploadFileInput', image, image.name);
    }

    formData.append('idUpload', this.idUpload);
    formData.append('app', 'ObjectDetection');

    return this.http.post<ResponseUploadMultipleFiles[]>(
      `${this.BASE_URL}/Common/UploadMultipleFiles`,
      formData
    );
  }

  /**
   * Uploads files by a given link.
   *
   * @param {string} link - The URL of the file to be uploaded.
   * @returns {Observable<ResponseUploadMultipleFiles[]>} - An Observable that emits an array of `ResponseUploadMultipleFiles` objects upon successful upload.
   */
  uploadByLink(link: string): Observable<ResponseUploadMultipleFiles[]> {
    const formData: FormData = new FormData();
    if (!this.idUpload) this.idUpload = this.generateUUID();

    formData.append('idUpload', this.idUpload);
    formData.append(`link_${this.generateUUID()}`, link);
    formData.append('app', '');

    return this.http.post<ResponseUploadMultipleFiles[]>(
      `${this.BASE_URL}/Common/UploadMultipleFiles`,
      formData
    );
  }

  /**
   * Fetches image information based on the provided file name.
   *
   * @param {string} fileName - The name of the file to fetch information for.
   * @returns {Observable<ResponseGetImageInfo>} An Observable containing the image information.
   */
  getImageInfo(fileName: string): Observable<ResponseGetImageInfo> {
    const params = new HttpParams()
      .append('uploadId', this.idUpload)
      .append('fileName', fileName)
      .append('app', '');

    return this.http.get<ResponseGetImageInfo>(
      `${this.BASE_URL}/GetImageInfo`,
      { params }
    );
  }

  DetectAndDrawObjectsBounds(
    name: string,
    options?: DetectionOptions | null
  ): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('idMain', this.idUpload);
    formData.append('FileName', name);
    formData.append('WithProgress', 'true');
    formData.append(
      'includeLabel',
      options?.includeLabel?.toString() ?? 'true'
    );
    formData.append(
      'includeScore',
      options?.includeScore?.toString() ?? 'true'
    );
    formData.append('threshold', options?.threshold ?? '50');
    formData.append('Application', 'Image');

    if (options?.color) {
      formData.append('color', options.color);
    }

    return this.http.post(
      `${this.BASE_URL}/DetectAndDrawObjectsBounds`,
      formData
    );
  }

  GetThumbnail(fileName: string) {
    const params = new HttpParams()
      .append('uploadId', this.idUpload)
      .append('fileName', fileName);

    return this.http.get(`${this.BASE_URL}/GetThumbnail`, {
      params,
      responseType: 'arraybuffer',
    });
  }

  /**
   * Retrieves an image preview for a given file name.
   *
   * @param {string} fileName - The name of the file for which to retrieve the image preview.
   * @returns {Observable<any>} An Observable that represents the image preview retrieval operation.
   */
  GetImagePreview(fileName: string, pageIndex = 0) {
    return this.http.post<any>(
      `${this.BASE_URL}/GetImagePreview`,
      this.createFormData({
        idMain: this.idUpload,
        Application: 'ObjectDetection',
        FileName: fileName,
        CurrentPage: pageIndex ? pageIndex + 1 : 1,
      })
    );
  }

  /**
   * Downloads a file based on the provided request parameters.
   *
   * @param {GetDownloadFileRequest} payload - The request parameters for downloading the file.
   * @returns {Observable<any>} An Observable that represents the file download operation.
   */

  DownloadFile(payload: GetDownloadFileRequest) {
    const params = this.buildHttpParams(payload);

    return this.http.get(`${this.BASE_URL}/Common/DownloadFile`, { params });
  }

  /**
   * Downloads multiple files based on the provided request parameters.
   *
   * @param {GetDownloadFilesRequest} payload - The request parameters for downloading multiple files.
   * @returns {Observable<any>} An Observable that represents the multiple file download operation.
   */

  DownloadFiles(payload: GetDownloadFilesRequest) {
    const params = this.buildHttpParams(payload);

    return this.http.get(`${this.BASE_URL}/Common/DownloadFiles`, { params });
  }

  /**
   * Retrieves a download link based on the provided request parameters.
   *
   * @param {GetDownloadLinkRequest} payload - The request parameters for retrieving the download link.
   * @returns {Observable<any>} An Observable that represents the download link retrieval operation.
   */

  GetDownloadLink(payload: GetDownloadLinkRequest): Observable<any> {
    let params = new HttpParams().append('id', payload.id);
    payload.files.forEach((file) => {
      params = params.append('files', file);
    });

    return this.http.get(`${this.BASE_URL}/Common/GetDownloadLink`, { params });
  }

  /**
   * Cancels a previously initiated operation.
   *
   * @returns {Observable<any>} An Observable that represents the cancellation operation.
   */

  CancelOperation() {
    return this.http.post(`${this.BASE_URL}/Common/CancelOperation`, {
      id: this.idUpload,
    });
  }

  /**
   * Uploads a file from Google Drive using a provided payload.
   *
   * @param {any} payload - The payload containing the necessary data for the upload.
   * @returns {Observable<any>} An Observable that represents the upload operation.
   */
  UploadFromGoogleDisk(payload: any): Observable<any> {
    if (!this.idUpload) this.idUpload = this.generateUUID();

    const IdUpload = this.idUpload;

    return this.http.post(
      `${this.BASE_URL}/Common/UploadFromGoogleDisk`,
      this.createFormData({
        IdUpload,
        ...payload,
      })
    );
  }

  /**
   * Uploads a file from Dropbox using a provided payload.
   *
   * @param {any} payload - The payload containing the necessary data for the upload.
   * @returns {Observable<any>} An Observable that represents the upload operation.
   */
  UploadFromDropbox(payload: any): Observable<any> {
    if (!this.idUpload) this.idUpload = this.generateUUID();

    const IdUpload = this.idUpload;

    return this.http.post(
      `${this.BASE_URL}/Common/UploadFromDropbox`,
      this.createFormData({
        IdUpload,
        ...payload,
      })
    );
  }

  GetStatus(operationId: string): Observable<any> {
    return this.http.get<any>(
      `${this.BASE_URL}/Common/GetStatus?id=${encodeURIComponent(operationId)}`
    );
  }

  /**
   * Builds an HttpParams object from a given object, handling various value types.
   *
   * @param {Object.<string, any>} obj - The object containing key-value pairs to be included in the HttpParams.
   * @returns {HttpParams} An HttpParams object containing the key-value pairs from the input object.
   * @private
   */
  private buildHttpParams(obj: Record<string, any>): HttpParams {
    let params = new HttpParams();
    for (const key in obj) {
      const value = obj[key];
      if (value === null || value === undefined || value === '') {
        continue;
      }
      if (typeof value === 'object') {
        params = params.set(key, JSON.stringify(value));
      } else {
        params = params.set(key, value);
      }
    }

    return params;
  }

  /**
   * Creates FormData from a nested object.
   *
   * @param {Record<string, any>} object - The object to convert into FormData.
   * @param {FormData} [form] - The FormData to append data to. If not provided, a new FormData instance will be created.
   * @param {string} [namespace] - The namespace for properties in the FormData.
   * @returns {FormData} The FormData object containing the data from the provided object.
   * @private
   */
  private createFormData(
    object: Record<string, any>,
    form?: FormData,
    namespace?: string
  ): FormData {
    const formData = form || new FormData();
    for (const property in object) {
      if (
        !Object.prototype.hasOwnProperty.call(object, property) ||
        !object[property]
      ) {
        continue;
      }
      const formKey = namespace ? `${namespace}[${property}]` : property;
      if (object[property] instanceof Date) {
        formData.append(formKey, object[property].toISOString());
      } else if (
        typeof object[property] === 'object' &&
        !(object[property] instanceof File)
      ) {
        this.createFormData(object[property], formData, formKey);
      } else {
        formData.append(formKey, object[property]);
      }
    }

    return formData;
  }

  CreateGoogleDriveFolder(token: string) {
    return this.http.post(
      `${this.BASE_URL}/Common/CreateGoogleDriveFolderIfNotExist`,
      this.createFormData({
        token,
      })
    );
  }

  UploadToGoogleDisk(
    googleFolderId: string,
    files: string[],
    Token: string
  ): Promise<boolean> {
    return new Promise<any>((resolve) => {
      if (!googleFolderId || !files || !files.length) {
        resolve(false);
      } else {
        const fileNamesQuery = files
          .map((fileName) => 'files=' + encodeURIComponent(fileName))
          .join('&');
        this.http
          .post(
            `${this.BASE_URL}/Common/UploadToGoogleDisk?folderId=${googleFolderId}&uploadId=Image/${this.idUpload}&${fileNamesQuery}`,
            this.createFormData({
              Token,
            })
          )
          .subscribe(() => {
            resolve(true);
          });
      }
    });
  }

  sendDownloadLink(
    url: string,
    email: string,
    locale: string
  ): Observable<any> {
    return this.http.post<any>(
      `/imaging/SendUrlToEmail/imaging/image`,
      this.createFormData({
        url,
        email,
        locale,
      })
    );
  }

  generateImages(payload: GenerateImagesRequest) {
    const formData = this.createFormData(payload);
    this.idUpload = this.generateUUID();

    formData.append('idMain', this.idUpload);
    formData.append('WithProgress', 'true');

    return this.http.post<any>(`${this.BASE_URL}/GenerateImages`, formData);
  }
}
